import { useEffect, useState } from "react";
import Request from "src/api";
import { Routes } from "src/constant/api_url";

// Character limits for the admin panel's input fields, served from
// config/adminKeyValueLimit.json in the Node repo via GET /get-admin-key-value-limit.
//
// This is NOT the member app's keyValueLimit.json, which the backend delivers on
// login / token check. The admin panel is Privilege-only, so there is a single map
// with no prime/privilege split.
//
// Deliberately NOT cached on this side: the limits are re-fetched from the server
// every time, so a corrected value in adminKeyValueLimit.json is picked up on the
// next page load without anyone needing to clear storage or restart a service.
//
// The only thing kept between calls is the in-flight promise. That is request
// coalescing, not a cache — it holds no result after settling (cleared in         
// `finally`) and exists only so the many <LimitedTextField> instances on a page
// don't each fire their own request for identical data.
let inFlightRequest = null;

export const fetchAdminKeyValueLimits = async () => {
  // Collapse concurrent callers onto a single request.
  if (inFlightRequest) return inFlightRequest;

  inFlightRequest = (async () => {
    try {
      const res = await Request.get(Routes.GETADMINKEYVALUELIMIT);
      const data = res?.data;
      return data && typeof data === "object" ? data : {};
    } catch (e) {
      // Fail open: forms still render, just without a length cap.
      return {};
    } finally {
      inFlightRequest = null;
    }
  })();

  return inFlightRequest;
};

/**
 * Loads the admin character limits for this component instance.
 *
 * Not cached — every instance re-fetches, so a limit corrected in
 * config/adminKeyValueLimit.json takes effect on the next load.
 *
 * @returns {{ limits: Object, ready: boolean, getLimit: (key: string) => (number|undefined) }}
 *   `ready` is false until the fetch settles, so a form can avoid flashing a
 *   counter before the limit is known.
 */
export default function useAdminLimits() {
  const [limits, setLimits] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAdminKeyValueLimits().then((data) => {
      if (cancelled) return;
      setLimits(data.data);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const getLimit = (key) => {
    const value = limits?.[key];
    return typeof value === "number" && value > 0 ? value : undefined;
  };

  return { limits, ready, getLimit };
}